import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  X, Search, Plus, Trash2, Upload, FileText, Loader2, CheckCircle2,
} from "lucide-react";

// Modal — Admin creates a new invoice for a client.
// Two modes: "generate" (line-item form) OR "upload" (PDF attach). Both flows
// share the same top: pick a client, set title / due date. Then either save as
// draft or "Save & send" — the send flow triggers the client-facing notify.
export default function NewInvoiceModal({ open, onClose, onCreated, prefillClientId }) {
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);       // for retail auto-fill on line items
  const [clientId, setClientId] = useState(prefillClientId || "");
  const [clientQuery, setClientQuery] = useState("");
  const [mode, setMode] = useState("generate");   // generate | upload
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notesClient, setNotesClient] = useState("");
  const [taxRate, setTaxRate] = useState(0);
  // Discount is a fixed $ by default; admin can toggle to % at their discretion.
  const [discountMode, setDiscountMode] = useState("amount");  // "amount" | "percent"
  const [discount, setDiscount] = useState(0);
  const [lineItems, setLineItems] = useState([{ description: "", qty: 1, unit_price: 0, material_id: null }]);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Only show approved clients so we can't accidentally invoice someone
    // who hasn't been through the approval queue.
    api.get("/clients").then((r) => {
      const approved = r.data.filter((c) => c.access_level === "approved");
      setClients(approved);
    }).catch(() => {});
    // Pull the material catalog once so line items can auto-fill retail price
    // when the admin picks a SKU. Uses the /materials/margins endpoint so we
    // get retail_usd in the same shape used everywhere else.
    api.get("/materials").then((r) => setMaterials(r.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open && prefillClientId) setClientId(prefillClientId);
  }, [open, prefillClientId]);

  const selectedClient = clients.find((c) => c.user_id === clientId) || null;

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => (
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.customer_code || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q)
    ));
  }, [clientQuery, clients]);

  const subtotal = lineItems.reduce(
    (a, li) => a + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0),
    0,
  );
  // Resolve the discount dollar-amount based on mode. Percent mode multiplies
  // subtotal by the percentage the admin entered (0 – 100).
  const discountAmount = discountMode === "percent"
    ? subtotal * Math.min(100, Math.max(0, parseFloat(discount) || 0)) / 100
    : Math.max(0, parseFloat(discount) || 0);
  const afterDiscount = Math.max(0, subtotal - discountAmount);
  const taxAmount     = afterDiscount * (parseFloat(taxRate) || 0);
  const grandTotal    = afterDiscount + taxAmount;

  const setLi = (i, key, v) =>
    setLineItems((items) => items.map((li, idx) => (idx === i ? { ...li, [key]: v } : li)));
  const addLi = () => setLineItems((items) => [...items, { description: "", qty: 1, unit_price: 0, material_id: null }]);
  const delLi = (i) => setLineItems((items) => items.filter((_, idx) => idx !== i));

  // Auto-fill a line from a chosen inventory SKU. Retail price + name flow in;
  // qty stays user-controlled. We keep material_id on the line so the invoice
  // stores the link back to the source SKU (useful for future margin reports).
  const pickMaterial = (i, material_id) => {
    if (!material_id) {
      setLi(i, "material_id", null);
      return;
    }
    const m = materials.find((x) => x.material_id === material_id);
    if (!m) return;
    setLineItems((items) => items.map((li, idx) => idx !== i ? li : {
      ...li,
      material_id,
      description: li.description || m.name,
      unit_price: Number(m.retail_usd) || Number(li.unit_price) || 0,
    }));
  };

  const reset = () => {
    setClientId(prefillClientId || "");
    setClientQuery("");
    setMode("generate");
    setTitle("");
    setDueDate("");
    setNotesClient("");
    setTaxRate(0);
    setDiscount(0);
    setLineItems([{ description: "", qty: 1, unit_price: 0, material_id: null }]);
    setPdfFile(null);
    setSaving(false);
    setDiscountMode("amount");
  };

  const submit = async (sendNow) => {
    if (!clientId) { toast.error("Select a client"); return; }
    if (!title.trim()) { toast.error("Add an invoice title"); return; }
    if (mode === "generate") {
      const clean = lineItems.filter((li) => (li.description || "").trim());
      if (clean.length === 0) { toast.error("Add at least one line item"); return; }
      if (grandTotal <= 0) { toast.error("Total must be greater than $0"); return; }
    } else if (mode === "upload" && !pdfFile) {
      toast.error("Choose the invoice PDF to upload");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        user_id: clientId,
        title:   title.trim(),
        due_date: dueDate || null,
        notes_client: notesClient.trim(),
      };
      if (mode === "generate") {
        payload.line_items = lineItems
          .filter((li) => (li.description || "").trim())
          .map((li) => ({
            description: li.description,
            qty: parseFloat(li.qty) || 0,
            unit_price: parseFloat(li.unit_price) || 0,
            material_id: li.material_id || null,
          }));
        payload.tax_rate = parseFloat(taxRate) || 0;
        // Backend expects a dollar discount. If admin entered a percentage,
        // resolve it here so the server-side math stays authoritative.
        payload.discount = Math.round(discountAmount * 100) / 100;
      } else {
        // Uploaded PDF — no line items, take the amount from the entered "total".
        payload.amount = grandTotal || parseFloat(discount) || 0;
      }
      const r = await api.post("/invoices", payload);
      const created = r.data;

      // If PDF, attach it now.
      if (mode === "upload" && pdfFile) {
        const form = new FormData();
        form.append("file", pdfFile);
        await api.post(`/invoices/${created.invoice_id}/attach-pdf`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (sendNow) {
        await api.post(`/invoices/${created.invoice_id}/send`);
      }
      toast.success(sendNow
        ? `Invoice ${created.invoice_id} sent to client`
        : `Invoice ${created.invoice_id} saved as draft`);
      onCreated?.(created);
      reset();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="new-invoice-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white dark:bg-slate-900 w-full sm:max-w-3xl rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[86vh] overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-[#E2E8F0] dark:border-slate-700">
          <div>
            <p className="overline">Billing</p>
            <h2 className="font-heading text-xl font-medium text-[#0A2540] dark:text-white">New invoice</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="new-invoice-close"
            className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-[#475569] dark:text-slate-400"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-6">
          {/* 1. Client picker */}
          <section>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-2">
              1. Bill to
            </label>
            {selectedClient ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border-2 border-emerald-500 dark:border-emerald-600 bg-emerald-50/40 dark:bg-emerald-900/10 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0A2540] to-[#12365b] text-white grid place-items-center font-heading font-semibold uppercase shrink-0">
                    {(selectedClient.name || selectedClient.email || "?").trim().charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-[#0A2540] dark:text-white truncate" data-testid="picked-client-name">
                      {selectedClient.name || "Unnamed"}
                    </div>
                    <div className="text-xs text-[#475569] dark:text-slate-400 truncate">
                      {selectedClient.email}
                      {selectedClient.customer_code && (<> · <span className="font-mono text-[#C9A356]">{selectedClient.customer_code}</span></>)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setClientId(""); setClientQuery(""); }}
                  data-testid="change-client"
                  className="text-xs font-semibold text-[#C9A356] hover:text-[#DDBD77] shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="rounded-lg border border-[#E2E8F0] dark:border-slate-700 overflow-hidden">
                <div className="relative border-b border-[#E2E8F0] dark:border-slate-700">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <input
                    autoFocus
                    value={clientQuery}
                    onChange={(e) => setClientQuery(e.target.value)}
                    placeholder="Search clients by name, email, phone, or PB-code"
                    data-testid="client-search"
                    className="w-full pl-9 pr-3 h-11 bg-transparent text-sm placeholder:text-[#94A3B8] dark:text-white focus:outline-none"
                  />
                </div>
                {clients.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-[#475569] dark:text-slate-400 text-center">
                    Loading approved clients…
                  </p>
                ) : filteredClients.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-[#475569] dark:text-slate-400 text-center" data-testid="no-clients-match">
                    No approved clients match "<strong>{clientQuery}</strong>"
                  </p>
                ) : (
                  <ul className="max-h-56 overflow-y-auto" data-testid="client-list">
                    {filteredClients.map((c) => (
                      <li key={c.user_id}>
                        <button
                          type="button"
                          onClick={() => setClientId(c.user_id)}
                          data-testid={`pick-client-${c.user_id}`}
                          className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-700 last:border-b-0 transition"
                        >
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0A2540] to-[#12365b] text-white grid place-items-center font-heading text-sm font-semibold uppercase shrink-0">
                            {(c.name || c.email || "?").trim().charAt(0)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">
                              {c.name || "Unnamed"}
                            </div>
                            <div className="text-xs text-[#475569] dark:text-slate-400 truncate">
                              {c.email}
                              {c.customer_code && (<> · <span className="font-mono text-[#C9A356]">{c.customer_code}</span></>)}
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-900/60 text-[11px] text-[#94A3B8] border-t border-[#E2E8F0] dark:border-slate-700">
                  Showing {filteredClients.length} of {clients.length} approved clients
                </div>
              </div>
            )}
          </section>

          {/* 2. Basics */}
          <section className="grid sm:grid-cols-[1fr_180px] gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-2">
                2. Invoice title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Solar Install — Phase 1"
                data-testid="new-inv-title"
                className="w-full h-11 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-2">
                Due date
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="new-inv-due"
                className="w-full h-11 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 text-sm"
              />
            </div>
          </section>

          {/* 3. Mode toggle */}
          <section>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-2">
              3. Invoice content
            </label>
            <div className="inline-flex rounded-lg border border-[#E2E8F0] dark:border-slate-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setMode("generate")}
                data-testid="mode-generate"
                className={`px-4 h-10 text-sm font-semibold ${mode === "generate" ? "bg-[#0A2540] text-white" : "bg-white dark:bg-slate-900 text-[#475569] dark:text-slate-400"}`}
              >
                Generate line items
              </button>
              <button
                type="button"
                onClick={() => setMode("upload")}
                data-testid="mode-upload"
                className={`px-4 h-10 text-sm font-semibold ${mode === "upload" ? "bg-[#0A2540] text-white" : "bg-white dark:bg-slate-900 text-[#475569] dark:text-slate-400"}`}
              >
                Upload PDF
              </button>
            </div>
          </section>

          {mode === "generate" ? (
            <section className="rounded-lg border border-[#E2E8F0] dark:border-slate-700 p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
                    <th className="text-left pb-2">Description</th>
                    <th className="text-right pb-2 w-20">Qty</th>
                    <th className="text-right pb-2 w-28">Unit price</th>
                    <th className="text-right pb-2 w-24">Subtotal</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, i) => {
                    const sub = (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0);
                    return (
                      <tr key={i}>
                        <td className="pr-2 pb-2">
                          <div className="flex gap-1">
                            <select
                              value={li.material_id || ""}
                              onChange={(e) => pickMaterial(i, e.target.value)}
                              data-testid={`new-li-material-${i}`}
                              className="h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-2 text-xs w-32 shrink-0"
                              title="Pick from inventory to auto-fill retail price"
                            >
                              <option value="">From inventory…</option>
                              {materials.map((m) => (
                                <option key={m.material_id} value={m.material_id}>
                                  {m.name}{m.sku ? ` · ${m.sku}` : ""}
                                </option>
                              ))}
                            </select>
                            <input
                              value={li.description}
                              onChange={(e) => setLi(i, "description", e.target.value)}
                              placeholder="Service / item"
                              data-testid={`new-li-desc-${i}`}
                              className="flex-1 min-w-0 h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 text-sm"
                            />
                          </div>
                        </td>
                        <td className="pr-2 pb-2">
                          <input
                            type="number" step="0.01" min="0"
                            value={li.qty}
                            onChange={(e) => setLi(i, "qty", e.target.value)}
                            data-testid={`new-li-qty-${i}`}
                            className="w-full h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-2 text-sm text-right"
                          />
                        </td>
                        <td className="pr-2 pb-2">
                          <input
                            type="number" step="0.01" min="0"
                            value={li.unit_price}
                            onChange={(e) => setLi(i, "unit_price", e.target.value)}
                            data-testid={`new-li-price-${i}`}
                            className="w-full h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-2 text-sm text-right"
                          />
                        </td>
                        <td className="pr-2 pb-2 text-right tabular-nums">${sub.toFixed(2)}</td>
                        <td className="pb-2 text-right">
                          {lineItems.length > 1 && (
                            <button
                              type="button" onClick={() => delLi(i)}
                              className="text-red-500 hover:text-red-600"
                              data-testid={`new-li-del-${i}`}
                              aria-label="Remove line"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button
                type="button" onClick={addLi}
                data-testid="new-li-add"
                className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#C9A356] hover:text-[#DDBD77]"
              >
                <Plus className="w-3 h-3" /> Add line item
              </button>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <label className="text-xs text-[#475569] dark:text-slate-400">
                  Tax rate
                  <select
                    value={taxRate}
                    onChange={(e) => setTaxRate(parseFloat(e.target.value))}
                    data-testid="new-tax"
                    className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-2 text-sm"
                  >
                    <option value="0">No tax</option>
                    <option value="0.05">5% (SXM TOT)</option>
                    <option value="0.0625">6.25%</option>
                    <option value="0.10">10%</option>
                  </select>
                </label>
                <label className="text-xs text-[#475569] dark:text-slate-400">
                  <div className="flex items-center justify-between mb-1">
                    <span>Discount ({discountMode === "percent" ? "%" : "$"})</span>
                    <div className="inline-flex rounded-md border border-[#E2E8F0] dark:border-slate-700 overflow-hidden text-[10px] font-semibold" data-testid="new-discount-toggle">
                      <button
                        type="button"
                        onClick={() => setDiscountMode("amount")}
                        className={`px-2 py-0.5 transition ${discountMode === "amount" ? "bg-[#0A2540] text-white" : "text-[#475569] hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                      >$</button>
                      <button
                        type="button"
                        onClick={() => setDiscountMode("percent")}
                        className={`px-2 py-0.5 transition border-l border-[#E2E8F0] dark:border-slate-700 ${discountMode === "percent" ? "bg-[#0A2540] text-white" : "text-[#475569] hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                      >%</button>
                    </div>
                  </div>
                  <input
                    type="number" step="0.01" min="0" max={discountMode === "percent" ? 100 : undefined}
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    data-testid="new-discount"
                    className="w-full h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 text-sm"
                  />
                </label>
              </div>

              {/* Totals preview */}
              <div className="mt-4 pt-3 border-t border-[#E2E8F0] dark:border-slate-700 space-y-1 text-sm">
                <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                {discountAmount > 0 && (
                  <Row
                    label={discountMode === "percent" ? `Discount (${parseFloat(discount) || 0}%)` : "Discount"}
                    value={`–$${discountAmount.toFixed(2)}`}
                  />
                )}
                {taxAmount > 0 && <Row label={`Tax (${((taxRate) * 100).toFixed(2)}%)`} value={`$${taxAmount.toFixed(2)}`} />}
                <div className="flex justify-between items-center pt-2 text-base font-bold text-[#0A2540] dark:text-white">
                  <span>Total</span>
                  <span className="tabular-nums" data-testid="new-inv-total">${grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </section>
          ) : (
            <section className="rounded-lg border-2 border-dashed border-[#E2E8F0] dark:border-slate-700 p-6 text-center">
              <input
                id="new-invoice-pdf"
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="new-inv-pdf-input"
              />
              <label htmlFor="new-invoice-pdf" className="inline-flex flex-col items-center gap-2 cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-[#C9A356]/15 grid place-items-center text-[#C9A356]">
                  <Upload className="w-5 h-5" />
                </div>
                <div className="text-sm text-[#0A2540] dark:text-white font-medium">
                  {pdfFile ? (
                    <span className="inline-flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-[#C9A356]" /> {pdfFile.name}
                    </span>
                  ) : "Click to choose a PDF or image"}
                </div>
                <div className="text-xs text-[#475569] dark:text-slate-400">Max 15 MB. Client will see it inline.</div>
              </label>

              {/* Even when uploading a PDF we still capture the total so the client can pay it. */}
              <div className="mt-6 max-w-xs mx-auto">
                <label className="block text-left text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1">
                  Invoice total ($)
                </label>
                <input
                  type="number" step="0.01" min="0"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="e.g. 1250.00"
                  data-testid="new-upload-amount"
                  className="w-full h-11 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 text-sm text-right"
                />
                <p className="mt-1 text-[11px] text-[#94A3B8] text-left">
                  So the client can pay it — matches the PDF total.
                </p>
              </div>
            </section>
          )}

          {/* Notes */}
          <section>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-2">
              Notes visible to client (optional)
            </label>
            <textarea
              rows={2}
              value={notesClient}
              onChange={(e) => setNotesClient(e.target.value)}
              placeholder="Anything the client should know: payment terms, project reference, etc."
              data-testid="new-inv-notes"
              className="w-full rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 py-2 text-sm"
            />
          </section>
        </div>

        {/* Footer actions */}
        <footer className="px-5 sm:px-6 py-4 border-t border-[#E2E8F0] dark:border-slate-700 flex flex-col sm:flex-row-reverse gap-2">
          <button
            onClick={() => submit(true)}
            disabled={saving}
            data-testid="new-inv-save-send"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Save &amp; send to client
          </button>
          <button
            onClick={() => submit(false)}
            disabled={saving}
            data-testid="new-inv-save-draft"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md border border-[#E2E8F0] dark:border-slate-700 hover:border-[#0A2540] dark:hover:border-white text-[#0A2540] dark:text-white font-semibold text-sm transition disabled:opacity-60"
          >
            Save as draft
          </button>
        </footer>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[#475569] dark:text-slate-400">
      <span>{label}</span>
      <span className="text-[#0A2540] dark:text-white tabular-nums">{value}</span>
    </div>
  );
}
